// Phase 22 — Global cart helper. Single source of truth for the public cart
// stored in localStorage. Any write triggers a `cart:updated` CustomEvent so
// the PublicHeader badge (and any other listener) re-renders instantly
// without a page refresh.
//
// API:
//   getCart()                 → { items: [...] }
//   setCart(cart)             → persist + broadcast
//   addToCart(product, qty=1) → { ok, count }
//   removeFromCart(slug)      → { ok }
//   updateQty(slug, qty)      → { ok }
//   clearCart()               → { ok }
//   cartCount(cart?)          → total item quantity number

export const CART_KEY = "latm_cart_v1";
const EVENT_NAME = "cart:updated";

function safeParse(raw) {
  try { return raw ? JSON.parse(raw) : null; } catch { return null; }
}

export function getCart() {
  if (typeof window === "undefined") return { items: [] };
  const c = safeParse(localStorage.getItem(CART_KEY));
  if (!c || !Array.isArray(c.items)) return { items: [] };
  return c;
}

export function setCart(cart) {
  const next = { items: Array.isArray(cart?.items) ? cart.items : [] };
  try { localStorage.setItem(CART_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  broadcast(next);
  return next;
}

export function cartCount(cart) {
  const c = cart || getCart();
  return (c.items || []).reduce((acc, i) => acc + (Number(i.qty) || 0), 0);
}

export function addToCart(product, qty = 1) {
  if (!product) return { ok: false, count: cartCount() };
  const slug = product.slug || product.handle || product.id;
  if (!slug) return { ok: false, count: cartCount() };

  const priceCents = product.price_cents
    ?? Math.round((product.price || 0) * 100);
  const media = (product.media || []).filter(Boolean);
  // Section 3 — Variant lock. Different variants of the same product are
  // separate cart lines, keyed by `slug + variant_id`. Variant metadata
  // travels all the way through checkout to Square + admin order detail.
  const variantId = product.variant_id || null;
  const cartKey = variantId ? `${slug}:${variantId}` : slug;

  const cart = getCart();
  const existing = cart.items.find((i) => (i.cart_key || i.slug) === cartKey);
  if (existing) {
    existing.qty = (Number(existing.qty) || 0) + qty;
  } else {
    cart.items.push({
      slug,
      cart_key: cartKey,
      title: product.title || slug,
      price_cents: priceCents,
      media: media.length ? media : [product.image].filter(Boolean),
      qty,
      // Variant fields (omit if no variant)
      variant_id: variantId,
      variant_title: product.variant_title || null,
      square_variation_id: product.square_variation_id || null,
      shopify_variant_id: product.shopify_variant_id || null,
      sku: product.sku || null,
      // Partner attribution (Section 2)
      partner_id: product.partner_id || product.source_owner_id || null,
      partner_slug: product.partner_slug || null,
      source_type: product.source_type || null,
    });
  }
  setCart(cart);

  // Fire universal add-to-cart tracking event (no-op if backend missing)
  try {
    const visitor =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("latm_visitor_id") || (() => {
            const v = "v-" + Math.random().toString(36).slice(2, 14);
            localStorage.setItem("latm_visitor_id", v);
            return v;
          })()
        : "anon";
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/track/add-to-cart`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ product_handle: slug, visitor_id: visitor }),
    }).catch(() => {});
  } catch { /* ignore */ }

  return { ok: true, count: cartCount(cart) };
}

export function removeFromCart(slug) {
  const cart = getCart();
  cart.items = cart.items.filter((i) => (i.cart_key || i.slug) !== slug);
  setCart(cart);
  return { ok: true };
}

export function updateQty(slug, qty) {
  const cart = getCart();
  cart.items = cart.items.map((i) =>
    (i.cart_key || i.slug) === slug ? { ...i, qty: Math.max(1, Number(qty) || 1) } : i,
  );
  setCart(cart);
  return { ok: true };
}

export function clearCart() {
  setCart({ items: [] });
  return { ok: true };
}

function broadcast(cart) {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(
      new CustomEvent(EVENT_NAME, { detail: { count: cartCount(cart), cart } }),
    );
  } catch { /* ignore */ }
}

/**
 * React hook: live cart count. Updates on:
 *   - mount (reads localStorage)
 *   - `cart:updated` custom event (same tab)
 *   - native `storage` event (other tabs)
 */
import { useEffect, useState } from "react";

export function useCartCount() {
  const [count, setCount] = useState(() => cartCount());
  useEffect(() => {
    const refresh = () => setCount(cartCount());
    refresh();
    const onCustom = (e) => {
      const c = e?.detail?.count;
      setCount(typeof c === "number" ? c : cartCount());
    };
    const onStorage = (e) => {
      if (!e || e.key === CART_KEY || e.key === null) refresh();
    };
    window.addEventListener(EVENT_NAME, onCustom);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onCustom);
      window.removeEventListener("storage", onStorage);
    };
  }, []);
  return count;
}
